/* Trade price sync: keeps the three-tier trade line (original / retail / trade) locked to
   the variant the shopper actually has selected. Rate comes from the block's data
   attribute (shop.metafields.nsi.trade_rate).

   REWRITTEN 2026-09-10 (Zac). The old version bound ONCE — it looked up [data-price] at
   DOMContentLoaded, hung a MutationObserver on that node, and scraped price TEXT out of
   it forever after. Two things broke it, and both are live on this store:

     1. BSS B2B replaces the price wrapper's innerHTML outright
        (bss-b2b-chunk-cp.js: `e.innerHTML = compareHTML + priceHTML + unitHTML`), so
        [data-price] is destroyed. The observer was left watching a DETACHED node and
        never fired again — the trade line froze on the price of whichever variant was
        resolved at page load, which is the one in ?variant=. The theme puts ?variant=
        into the URL itself via history.replaceState on every click
        (sections/static-product.liquid: "enableHistory": true), so the trap arms itself
        after a shopper's first variant click even when they arrived on a bare URL.

     2. The `nsi-trade-price--nosale` class, which is what keeps tier 2 GREY on a product
        that is not marked down, is written by Liquid for the FIRST rendered variant and
        was never touched again. Switch to a variant that IS marked down and the old code
        revealed tier 1 ($15.00) but left the stale --nosale class in place, so tier 2
        stayed grey when it should have gone green. The class and tier 1's visibility now
        come from the same boolean, recomputed on every change, so they cannot disagree.

   So: nothing is cached. Every update re-queries its nodes, re-attaches its observer if
   the node it was watching has been swapped out, and takes its numbers from the product
   JSON (via nsi-variant-price-sync.js) rather than scraping text. Falling back to text
   only happens where there is no variant to resolve, e.g. a collection card. */
(function () {
  'use strict';

  var MONEY = /\$\s*\d[\d,]*(?:\.\d+)?/g;

  function parseCents(text) {
    /* Take the LAST money token in the string, not "every digit in the string".
       The old version stripped all non-digits, so "$15.00 $11.95" (what BSS writes into
       the wrapper) collapsed to "15.0011.95" -> parseFloat -> 15.0011 -> $15.00, and a
       range like "$13.60 - $98.95" became $13.61. Current price is always the last
       figure in both the theme's markup and BSS's. */
    var s = String(text || '');
    var found = s.match(MONEY);
    if (!found || !found.length) return null;
    var n = parseFloat(found[found.length - 1].replace(/[^0-9.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : null;
  }
  function fmt(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* Afterpay messaging widget (square-placement, open shadow DOM) quotes retail/4 —
     for trade customers rewrite it to trade price/4, and keep it right when the
     widget re-renders itself on variant changes. */
  var apExpected = null, apMo = null;
  function updateAfterpay(tradeCents) {
    var p = document.querySelector('square-placement');
    if (!p || !p.shadowRoot) return;
    var para = p.shadowRoot.querySelector('p');
    if (!para) return;
    apExpected = fmt(Math.round(tradeCents / 4));
    if (para.textContent.indexOf(apExpected) === -1) {
      para.innerHTML = para.innerHTML.replace(/\$\d[\d,]*\.?\d*/, apExpected);
    }
    if (!apMo) {
      apMo = new MutationObserver(function () {
        var para2 = p.shadowRoot && p.shadowRoot.querySelector('p');
        if (para2 && apExpected && para2.textContent.indexOf(apExpected) === -1) {
          para2.innerHTML = para2.innerHTML.replace(/\$\d[\d,]*\.?\d*/, apExpected);
        }
      });
      apMo.observe(p.shadowRoot, { childList: true, subtree: true, characterData: true });
    }
  }

  function scopeOf(block) {
    return block.closest('#shopify-section-static-product') ||
           block.closest('[data-product-wrapper]') ||
           block.closest('[data-product-details]') ||
           null;
  }

  /* Every source node is looked up fresh, on every call. This is the whole point:
     the nodes below get replaced by other scripts while the page is open. */
  function nodes(block) {
    var host = block.parentElement || document;
    return {
      priceEl: host.querySelector('[data-price]'),
      compareEl: host.querySelector('[data-price-compare-at]'),
      was: block.querySelector('.nsi-trade-price__was'),
      now: block.querySelector('[data-nsi-trade-now]'),
      compareOut: block.querySelector('[data-nsi-trade-compare]')
    };
  }

  function render(block, cents, compareCents, partial) {
    var n = nodes(block);
    if (!n.was || !n.now || cents == null) return;
    /* parseFloat, not parseInt: a fractional BSS rate (12.5) would truncate to 12 here and
       silently disagree with the server-rendered figure beside it. */
    var rate = parseFloat(block.getAttribute('data-nsi-rate'));
    if (!isFinite(rate) || rate <= 0) return; // no rate, no rewrite — never guess a percentage

    /* matches the Liquid: price | times: factor | plus: 50 | divided_by: 100 (round half up) */
    var tradeCents = Math.round(cents * (100 - rate) / 100);
    var prefix = block.__nsiPrefix || '';

    n.was.textContent = prefix + fmt(cents);
    n.now.textContent = prefix + fmt(tradeCents);

    /* A PARTIAL update knows the price but not yet the compare-at (the seed built from
       ShopifyAnalytics before /products/<handle>.js lands). Its null compare-at is "unknown",
       NOT "not on sale" — treating it as the latter wiped the server-rendered struck price on
       every marked-down product load, and left it wiped for good if the fetch failed. Update
       the two figures we do know and leave tier 1 exactly as Liquid rendered it. */
    if (partial && compareCents == null) return;

    /* ONE boolean drives both the tier-1 reveal and the --nosale class, so the class can
       never describe a different variant than the numbers on screen. */
    var onSale = compareCents != null && compareCents > cents;
    if (n.compareOut) {
      if (onSale) n.compareOut.textContent = fmt(compareCents);
      n.compareOut.hidden = !onSale;
    }
    block.classList.toggle('nsi-trade-price--nosale', !onSale);

    if (block.__nsiIsPdp) updateAfterpay(tradeCents);
  }

  /* Fallback path: no resolved variant (collection card, or the JSON never arrived).
     Read whatever the price nodes currently say. */
  function updateFromDom(block) {
    var n = nodes(block);
    if (!n.priceEl) return;
    var cents = parseCents(n.priceEl.textContent);
    if (cents == null) return;
    render(block, cents, n.compareEl ? parseCents(n.compareEl.textContent) : null);
  }

  /* Re-attach the observers whenever the node they were watching has been swapped out.
     Called on every update, so a wrapper wipe costs us one beat, not the session. */
  function reattach(block) {
    var n = nodes(block);
    var want = [n.priceEl, n.compareEl];
    var live = block.__nsiWatched || [];
    var same = want.length === live.length;
    for (var i = 0; same && i < want.length; i++) if (want[i] !== live[i]) same = false;
    if (same && block.__nsiMo) return;
    if (block.__nsiMo) block.__nsiMo.disconnect();
    block.__nsiMo = new MutationObserver(function () { if (!block.__nsiAuthoritative) updateFromDom(block); });
    for (var j = 0; j < want.length; j++) {
      if (want[j]) block.__nsiMo.observe(want[j], { childList: true, subtree: true, characterData: true });
    }
    block.__nsiWatched = want;
  }

  function bind(block) {
    if (block.__nsiBound) return;
    block.__nsiBound = true;
    block.__nsiIsPdp = !!block.closest('#shopify-section-static-product');
    /* collection cards render "from $13.60" when the product's variants differ in price;
       keep that word when we are redrawing from DOM text rather than a chosen variant */
    var w = block.querySelector('.nsi-trade-price__was');
    block.__nsiPrefix = w && /^\s*from\s+/i.test(w.textContent) ? 'from ' : '';
    block.__nsiScope = scopeOf(block);

    reattach(block);
    updateFromDom(block);

    if (block.__nsiIsPdp) {
      // the Afterpay widget's script loads late — retry until its shadow content exists
      var tries = 0;
      var t = setInterval(function () {
        var p = document.querySelector('square-placement');
        if (p && p.shadowRoot && p.shadowRoot.querySelector('p')) {
          var c = parseCents(block.querySelector('[data-nsi-trade-now]').textContent);
          if (c != null) updateAfterpay(c);
          clearInterval(t);
        } else if (++tries > 40) clearInterval(t);
      }, 250);
    }
  }

  /* Authoritative path: nsi-variant-price-sync.js has resolved the selected variant and
     read its real price + compare-at out of the product JSON. */
  document.addEventListener('nsi:variant-price', function (e) {
    var d = e.detail || {};
    var scope = d.scope;
    var blocks = (scope || document).querySelectorAll('[data-nsi-trade]');
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      bind(b);
      if (scope && !scope.contains(b)) continue;
      b.__nsiAuthoritative = true;
      b.__nsiPrefix = '';                 // a variant is chosen, so "from" no longer applies
      reattach(b);                        // BSS may have swapped the nodes since last time
      render(b, d.price, d.compareAt, d.partial);
    }
  });

  function init() {
    var blocks = document.querySelectorAll('[data-nsi-trade]');
    for (var i = 0; i < blocks.length; i++) bind(blocks[i]);
    /* quickshop modals and section re-renders inject new trade lines after load */
    new MutationObserver(function () {
      var later = document.querySelectorAll('[data-nsi-trade]');
      for (var j = 0; j < later.length; j++) if (!later[j].__nsiBound) bind(later[j]);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
